import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FEATURES } from '../config/features';
import logoSvg from '../assets/logo.svg';

type NavItem =
  | { kind: 'link';    icon: string; label: string; path: string }
  | { kind: 'section'; label: string };

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, signOut, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // ── Role determination ───────────────────────────────────────────────
  // Three primary personas drive the entire UI:
  //   HR        — runs cycles, dispatches reviews, calibration, sign-off
  //   Manager   — reviews their team; may also be a reviewee themselves
  //   Employee  — receives reviews; mostly reviewer-via-magic-link
  // Reviewers (peer / direct_report / external) DON'T sign in — they
  // get the magic link and never see this layout.
  const role = user?.role ?? 'employee';
  const isHR       = ['superadmin', 'admin', 'hr_admin'].includes(role);
  const isManager  = !isHR && (role === 'leader' || Boolean(user?.isLeader));
  const isLeader   = Boolean(user?.isLeader);  // legacy flag (Round-Table track 2)

  // Close sidebar on outside click (mobile)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Sidebar IA — minimalist per role ────────────────────────────────
  // Each persona sees ONLY what their workflow needs. Aligned with the
  // OPTICS EPR PDF workflow (self → 360/manager → calibration → sign-off).
  //
  // HR        : full cycle management + setup. Also sees My Self-Review
  //             (HR are people too).
  // Manager   : their team's reviews + own self-review. No calibration,
  //             no setup, no cross-cycle reports.
  // Employee  : their own self-review. That's it.
  //             (Anonymous reviewers get magic links — never see this nav.)

  const navItems: NavItem[] = isHR
    ? [
        { kind: 'link', icon: 'dashboard',       label: 'Dashboard',        path: '/dashboard' },

        // ── HR primary workflow ──
        // FIRST entry = the cycle launcher. HR opens an EPR cycle from
        // here; clicking a row drills into the 7-step wizard console.
        // The "Step N" links below are cross-cycle action surfaces only
        // (e.g. "show me all cycles ready for calibration today") — they
        // are NOT the way to start a new cycle.
        { kind: 'section', label: 'EPR Cycle' },
        { kind: 'link', icon: 'play_circle',     label: 'Start / Manage Cycles',  path: '/cycles' },

        // Cross-cycle nav is for STEPS WHERE HR ACTS ACROSS CYCLES —
        // i.e. blockers that need attention across the project. Per-cycle
        // execution steps (1 EPR submission, 3 Consolidation, 7 Outcome)
        // intentionally have NO cross-cycle entry — they're performed
        // inside one cycle's workflow console and don't generalise.
        // If you add or remove an item here, update routes.ts AND ensure
        // the index page exists at the linked path. ENGINEERING.md §2.
        { kind: 'section', label: 'Cross-cycle Actions' },
        { kind: 'link', icon: 'groups',          label: '360 Review',    path: '/hr-v1/reviews' },
        { kind: 'link', icon: 'balance',         label: 'Calibration',   path: '/hr-v1/calibration' },
        { kind: 'link', icon: 'task_alt',        label: 'Appraisals',    path: '/hr-v1/appraisals' },
        { kind: 'link', icon: 'gavel',           label: 'Appeals',       path: '/hr-v1/appeals' },
        { kind: 'link', icon: 'bar_chart',       label: 'Reports',                path: '/reports' },

        { kind: 'section', label: 'My Work' },
        { kind: 'link', icon: 'edit_note',       label: 'My Self-Review',   path: '/self-review' },

        { kind: 'section', label: 'Setup' },
        { kind: 'link', icon: 'tune',            label: 'OPTICS Template',  path: '/templates' },
        { kind: 'link', icon: 'manage_accounts', label: 'Cycle Enrolment',  path: '/setup' },

        ...(isLeader && FEATURES.LEADER_EPR ? [{ kind: 'link', icon: 'bolt', label: 'Leader Round-Table', path: '/leader-review' } as NavItem] : []),
      ]
    : isManager
    ? [
        { kind: 'link', icon: 'dashboard',          label: 'Dashboard',       path: '/dashboard' },

        { kind: 'section', label: 'My Team' },
        { kind: 'link', icon: 'supervisor_account', label: 'Manager Review',  path: '/manager-review' },
        { kind: 'link', icon: 'bar_chart',          label: 'Team Reports',    path: '/reports' },

        { kind: 'section', label: 'My Work' },
        { kind: 'link', icon: 'edit_note',          label: 'My Self-Review',  path: '/self-review' },

        ...(isLeader && FEATURES.LEADER_EPR ? [{ kind: 'link', icon: 'bolt', label: 'Leader Round-Table', path: '/leader-review' } as NavItem] : []),
      ]
    : [
        // Employee — minimalist
        { kind: 'link', icon: 'dashboard', label: 'Dashboard',       path: '/dashboard' },

        { kind: 'section', label: 'My Work' },
        { kind: 'link', icon: 'edit_note', label: 'My Self-Review',  path: '/self-review' },
      ];

  // App-switcher targets pulled from env (Phase 1 fix — was hardcoded localhost).
  // Falls back to local dev defaults if env vars are missing.
  const otherApps = [
    {
      icon: 'track_changes',
      label: 'OKR Tracker',
      href: import.meta.env.VITE_OKR_URL ?? 'http://localhost:3000',
    },
    {
      icon: 'shield_person',
      label: 'Leadership Portal',
      href: import.meta.env.VITE_PORTAL_URL ?? 'http://localhost:3001',
    },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  const initial = (user?.name || user?.email || 'U')[0].toUpperCase();

  return (
    <div className="flex h-screen bg-stone-50 overflow-hidden">

      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        ref={sidebarRef}
        id="sidebar-nav"
        className={`
          fixed md:static inset-y-0 left-0 z-40
          w-60 bg-[#011132] flex flex-col flex-shrink-0
          transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Brand */}
        <div className="px-5 py-5 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-lg">
              <img src={logoSvg} alt="Logo" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <p className="text-white font-extrabold text-base leading-tight tracking-wide">Rascal EPR</p>
              <p className="text-white/60 text-xs leading-tight mt-0.5">Employee Performance Review</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {authLoading ? (
            // Skeleton nav items while auth loads
            <>
              {[100, 80, 90, 75, 85, 70].map((w, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="w-4.5 h-4.5 rounded bg-white/10 animate-pulse flex-shrink-0" style={{ width: 18, height: 18 }} />
                  <div className="h-3 rounded bg-white/10 animate-pulse" style={{ width: `${w}%` }} />
                </div>
              ))}
            </>
          ) : (
            <>
              {navItems.map((item, idx) => {
                if (item.kind === 'section') {
                  return (
                    <p
                      key={`section-${idx}`}
                      className="text-white/30 text-[10px] px-3 pt-3 pb-1 uppercase tracking-widest"
                    >
                      {item.label}
                    </p>
                  );
                }
                const { icon, label, path } = item;
                // Active matching: exact match OR prefix match, but with special-case for /reviews
                // so /reviews/new doesn't also light up /reviews.
                const isActive =
                  location.pathname === path ||
                  (path !== '/reviews' && location.pathname.startsWith(path + '/'));
                return (
                  <button
                    key={path}
                    onClick={() => { navigate(path); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left
                      ${isActive
                        ? 'bg-white/15 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    <span className="material-symbols-rounded flex-shrink-0" style={{ fontSize: 18 }}>{icon}</span>
                    {label}
                  </button>
                );
              })}

              {/* Other Apps */}
              <div className="pt-3 mt-2 border-t border-white/10">
                <p className="text-white/30 text-[10px] px-3 pb-2 uppercase tracking-widest">Other Apps</p>
                {otherApps.map(({ icon, label, href }) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <span className="material-symbols-rounded flex-shrink-0" style={{ fontSize: 18 }}>{icon}</span>
                    {label}
                  </a>
                ))}
              </div>
            </>
          )}
        </nav>

        {/* User footer */}
        <div className="px-4 py-4 border-t border-white/10 flex-shrink-0">
          {authLoading ? (
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-7 h-7 rounded-full bg-white/10 animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-2.5 rounded bg-white/10 animate-pulse w-3/4" />
                <div className="h-2 rounded bg-white/10 animate-pulse w-1/2" />
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-full bg-blue-500/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-300 text-xs font-bold">{initial}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-white text-xs font-medium truncate">{user?.name || user?.email}</p>
                  <p className="text-white/40 text-[10px] uppercase">{user?.role}</p>
                </div>
              </div>
              <button
                onClick={handleSignOut}
                className="w-full text-white/60 hover:text-white text-xs py-1.5 px-3 rounded border border-white/10 hover:bg-white/10 transition-colors"
              >
                Sign out
              </button>
            </>
          )}
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Top bar */}
        <header className="bg-white border-b border-stone-200 px-6 py-3.5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-1 rounded text-stone-500 hover:text-stone-900 hover:bg-stone-100"
              onClick={() => setSidebarOpen(o => !o)}
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={sidebarOpen}
              aria-controls="sidebar-nav"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 22 }}>menu</span>
            </button>
            <div>
              <h1 className="text-base font-bold text-stone-900">Rascal EPR</h1>
              <p className="text-xs text-stone-400">{today}</p>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-stone-50">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
